import { useState } from 'react';
import { Sparkles } from 'lucide-react';

import { PageHeader, PageShell } from '~/components/layout';
import { Textarea } from '~/components/form';
import {
  Alert,
  Badge,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Disclosure,
  EmptyState,
  FormActions,
} from '~/components/ui';
import { apiErrorMessage } from '~/lib/apiError';
import type { ApiErrorInput } from '~/lib/apiError';
import { useExecuteListingPayloadMutation } from '~/modules/query/ai/digitalAssistants';
import type {
  ExecuteListingPayloadRequest,
  ExecuteListingPayloadResponse,
  ExecuteListingPayloadResultItem,
} from '~/modules/query/ai/types';

const PLACEHOLDER = `{
  "assistantKey": "social_caption_listing",
  "input": {
    "agent": {
      "name": "Shane Torres",
      "email": "shanetorres@remax.net",
      "phone": "5159840222"
    },
    "listing": {
      "mls_number": "66166",
      "address": "811 NW Boulder Point Place",
      "city": "Ankeny",
      "state": "IA",
      "price": "540000",
      "property_type": "Residential",
      "bedrooms": "4",
      "bathrooms": "3",
      "square_feet": "1832",
      "description": "Welcome to this Boulder Brook ranch home..."
    },
    "user": {
      "full_name": "Jon Doe",
      "name": "Gambit Real Estate",
      "office_name": "RE/MAX Concepts-Ankeny"
    }
  }
}`;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The `details` array service-ai returns with a failed request, when it sent one. */
function errorDetails(error: unknown): unknown[] {
  if (!isRecord(error)) {
    return [];
  }

  const data = error.data;

  if (!isRecord(data) || !Array.isArray(data.details)) {
    return [];
  }

  return data.details;
}

function detailText(detail: unknown): string {
  if (typeof detail === 'string') {
    return detail;
  }

  if (isRecord(detail) && typeof detail.message === 'string') {
    return detail.message;
  }

  return JSON.stringify(detail) ?? String(detail);
}

function extractCaptionText(result: ExecuteListingPayloadResultItem): string {
  const output = result.result?.output;

  if (typeof output === 'string') {
    return output;
  }

  if (output !== undefined) {
    return JSON.stringify(output, null, 2);
  }

  return 'The assistant returned no output.';
}

/**
 * Hidden developer tool: submit a listing payload (one item or an array) to the
 * digital-assistant caption runtime and inspect each result. Reachable by URL
 * only; it has no sidebar entry (overview.md, Routes).
 */
export default function CaptionTestPage() {
  const [payloadInput, setPayloadInput] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestErrorDetails, setRequestErrorDetails] = useState<unknown[]>([]);
  const [response, setResponse] = useState<ExecuteListingPayloadResponse | null>(null);

  const [executeListingPayload, { isLoading }] = useExecuteListingPayloadMutation();

  const handleRun = async () => {
    setParseError(null);
    setRequestError(null);
    setRequestErrorDetails([]);
    setResponse(null);

    let parsedPayload: unknown;
    try {
      parsedPayload = JSON.parse(payloadInput);
    } catch {
      setParseError('The payload is not valid JSON. Fix it and run the test again.');
      return;
    }

    try {
      // The service validates the pasted payload's shape, so a parsed result is
      // always `unknown` here and checked server-side.
      const result = await executeListingPayload(
        parsedPayload as ExecuteListingPayloadRequest,
      ).unwrap();
      setResponse(result);
    } catch (error) {
      setRequestError(
        apiErrorMessage(error as ApiErrorInput) ??
          'The caption service did not respond. Try again.',
      );
      setRequestErrorDetails(errorDetails(error));
    }
  };

  const handleClear = () => {
    setPayloadInput('');
    setParseError(null);
    setRequestError(null);
    setRequestErrorDetails([]);
    setResponse(null);
  };

  return (
    <PageShell>
      <PageHeader title="Caption test" breadcrumbs={[{ label: 'AI' }, { label: 'Caption test' }]} />

      <Card>
        <CardHeader
          title="Payload"
          description="Paste the { assistantKey, input } payload from the Social API, with input grouped as agent, listing and user, and run it against the caption service."
        />
        <CardBody className="space-y-4">
          <Textarea
            id="caption-test-payload"
            label="Payload JSON"
            value={payloadInput}
            onChange={setPayloadInput}
            rows={12}
            code
            placeholder={PLACEHOLDER}
            helperText="Paste one { assistantKey, input } object or an array of them."
            testId="caption-test-payload"
          />

          {parseError ? (
            <Alert variant="error">
              <p className="text-sm font-medium text-gray-800 dark:text-white/90">
                The payload is not valid JSON. Fix the syntax, then run it again.
              </p>
              <p className="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400">
                {parseError}
              </p>
            </Alert>
          ) : null}
        </CardBody>
        <CardFooter>
          <FormActions
            onDiscard={handleClear}
            discardLabel="Clear"
            onSave={handleRun}
            saveLabel="Run caption test"
            busyLabel="Running…"
            busy={isLoading}
            disabled={!payloadInput.trim()}
            disabledReason="Paste a payload first"
            saveTestId="caption-test-run"
            discardTestId="caption-test-clear"
          />
        </CardFooter>
      </Card>

      {requestError ? (
        <Alert variant="error">
          <p className="text-sm font-medium text-gray-800 dark:text-white/90">{requestError}</p>
          {requestErrorDetails.length > 0 ? (
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-gray-800 dark:text-white/90">
              {requestErrorDetails.map((detail, index) => (
                <li key={index}>{detailText(detail)}</li>
              ))}
            </ul>
          ) : null}
        </Alert>
      ) : null}

      {response?.results.map((result, index) => (
        <Card key={`${result.assistantKey}-${index}`}>
          <CardHeader
            title={result.assistantKey}
            description={`Request ${result.requestId}`}
            badge={
              <Badge color={result.success ? 'success' : 'error'} size="sm">
                {result.success ? 'Success' : `Failed (HTTP ${result.statusCode})`}
              </Badge>
            }
          />
          <CardBody className="space-y-4">
            {result.success ? (
              <p className="whitespace-pre-wrap rounded-lg border border-dashed border-gray-200 bg-white p-4 text-base leading-relaxed text-gray-800 dark:border-gray-800 dark:bg-gray-900 dark:text-white/90">
                {extractCaptionText(result)}
              </p>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-error-600 dark:text-error-400">
                  {apiErrorMessage({ error: result.error }) ??
                    'The assistant did not return a caption.'}
                </p>
                {Array.isArray(result.details) && result.details.length > 0 ? (
                  <ul className="list-disc space-y-1 pl-5 text-sm text-error-600 dark:text-error-400">
                    {result.details.map((detail, detailIndex) => (
                      <li key={detailIndex}>{detailText(detail)}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            )}

            <Disclosure title="Raw response" testId={`caption-test-raw-${index}`}>
              <div className="p-4">
                <pre className="max-h-96 overflow-auto rounded-lg bg-gray-50 p-3 font-mono text-xs text-gray-800 dark:bg-gray-950 dark:text-white/90">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </div>
            </Disclosure>
          </CardBody>
        </Card>
      ))}

      {response && response.results.length === 0 ? (
        <Card>
          <CardBody>
            <EmptyState
              icon={<Sparkles className="size-8" aria-hidden="true" />}
              title="No results returned"
              description="The service accepted the payload but returned no results to show."
            />
          </CardBody>
        </Card>
      ) : null}
    </PageShell>
  );
}
